import React, { Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { LanguageProvider, useLang } from "@/i18n/LanguageContext";
import { SUPPORTED_LOCALES, detectLocale } from "@/i18n/locale";
import { Layout } from "@/components/layout/Layout";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Technology from "@/pages/Technology";
import GovernanceOS from "@/pages/GovernanceOS";
import Solutions from "@/pages/Solutions";
import SolutionDetail from "@/pages/SolutionDetail";
import Research from "@/pages/Research";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Contact from "@/pages/Contact";
import PrivateBeta from "@/pages/PrivateBeta";
import NotFound from "@/pages/NotFound";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import Submissions from "@/pages/admin/Submissions";
import BlogAdmin from "@/pages/admin/BlogAdmin";

import InstitutionalPage from "@/pages/InstitutionalPage";
import { institutionalPages } from "@/data/institutional";
import Publications from "@/pages/Publications";
import PublicationDetail from "@/pages/PublicationDetail";
import Search from "@/pages/Search";
import PublicationsAdmin from "@/pages/admin/PublicationsAdmin";
import Documentation from "@/pages/Documentation";
import DocPage from "@/pages/DocPage";
import Universe from "@/pages/Universe";

// Root -> detected locale.
function RootRedirect() {
  return <Navigate to={`/${detectLocale()}`} replace />;
}

// Validates the locale segment, syncs language, and renders the localized site.
function LocalizedApp() {
  const { locale } = useParams();
  const { pathname } = useLocation();
  const { setLang } = useLang();
  const valid = SUPPORTED_LOCALES.includes(locale);

  useEffect(() => {
    if (valid) setLang(locale);
  }, [valid, locale, setLang]);

  if (!valid) return <Navigate to={`/${detectLocale()}${pathname}`} replace />;

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="about" element={<About />} />
        <Route path="universe" element={<Universe />} />
        <Route path="technology" element={<Technology />} />
        <Route path="ai-governance-os" element={<GovernanceOS />} />
        <Route path="solutions" element={<Solutions />} />
        <Route path="solutions/:slug" element={<SolutionDetail />} />
        <Route path="research" element={<Research />} />
        <Route path="blog" element={<Blog />} />
        <Route path="blog/:slug" element={<BlogPost />} />
        <Route path="contact" element={<Contact />} />
        <Route path="private-beta" element={<PrivateBeta />} />

        <Route path="documentation" element={<Documentation />} />
        <Route path="documentation/:slug" element={<DocPage />} />
        <Route path="trust-center" element={<InstitutionalPage config={institutionalPages.trustCenter} />} />
        <Route path="knowledge" element={<InstitutionalPage config={institutionalPages.knowledge} />} />
        <Route path="media" element={<InstitutionalPage config={institutionalPages.media} />} />
        <Route path="regulation" element={<InstitutionalPage config={institutionalPages.regulation} />} />
        <Route path="comparison" element={<InstitutionalPage config={institutionalPages.comparison} />} />
        <Route path="enterprise" element={<InstitutionalPage config={institutionalPages.enterprise} />} />
        <Route path="downloads" element={<InstitutionalPage config={institutionalPages.downloads} />} />
        <Route path="publications" element={<Publications />} />
        <Route path="publications/:slug" element={<PublicationDetail />} />
        <Route path="search" element={<Search />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <Toaster theme="dark" position="top-right" richColors />
          <Suspense fallback={<div className="min-h-screen bg-navy" />}>
            <Routes>
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="contacts" element={<Submissions kind="contact" />} />
                <Route path="beta" element={<Submissions kind="beta" />} />
                <Route path="blog" element={<BlogAdmin />} />
                <Route path="publications" element={<PublicationsAdmin />} />
              </Route>

              <Route path="/" element={<RootRedirect />} />
              <Route path="/:locale/*" element={<LocalizedApp />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
